import { NavLink, Outlet } from 'react-router-dom';
import type { AuthUser } from '@pantheon/shared';
import { RunnerLoginBanner } from '../components/RunnerLoginBanner';

interface LayoutProps {
  user: AuthUser | null;
  onLogout: () => void;
}

// Each agent carries its own accent dot so the nav reads at a glance; the
// utility pages get a neutral dot.
const AGENTS = [
  { to: '/', label: 'Dashboard', dot: 'bg-white/40', end: true },
  { to: '/analytics', label: 'Analytics', dot: 'bg-white/40' },
  { to: '/athena', label: 'Athena', dot: 'bg-athena' },
  { to: '/hermes', label: 'Hermes', dot: 'bg-hermes' },
  { to: '/daedalus', label: 'Daedalus', dot: 'bg-daedalus' },
  { to: '/argus', label: 'Argus', dot: 'bg-argus' },
  { to: '/prometheus', label: 'Prometheus', dot: 'bg-prometheus' },
];

const TOOLS = [
  { to: '/projects', label: 'Projects', dot: 'bg-white/40' },
  { to: '/costs', label: 'Costs', dot: 'bg-white/40' },
  { to: '/runners', label: 'Runners', dot: 'bg-white/40' },
  { to: '/settings', label: 'Settings', dot: 'bg-white/40' },
];

function NavItem({ to, label, dot, end }: { to: string; label: string; dot: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150 ${
          isActive
            ? 'bg-white/[0.12] text-white shadow-sm'
            : 'text-white/55 hover:text-white hover:bg-white/[0.06]'
        }`
      }
    >
      <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
      <span>{label}</span>
    </NavLink>
  );
}

export function Layout({ user, onLogout }: LayoutProps) {
  return (
    <div className="min-h-screen flex">
      {/* Left sidebar */}
      <aside className="w-60 shrink-0 bg-nav flex flex-col sticky top-0 h-screen">
        {/* Brand */}
        <div className="px-5 h-16 flex items-center gap-2.5 shrink-0">
          <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-primary/30">
            P
          </span>
          <span className="text-white font-bold text-base tracking-wide">Pantheon</span>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          {AGENTS.map((i) => (
            <NavItem key={i.to} {...i} />
          ))}
          <div className="my-3 mx-3 border-t border-white/10" />
          {TOOLS.map((i) => (
            <NavItem key={i.to} {...i} />
          ))}
        </nav>

        {/* User */}
        {user && (
          <div className="p-3 border-t border-white/10 shrink-0">
            <div className="flex items-center gap-2.5 px-2 py-1.5">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full ring-2 ring-white/15 shrink-0" />
              ) : (
                <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                  {(user.name || '?').slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-white text-sm font-medium truncate">{user.name}</div>
                <button
                  onClick={onLogout}
                  className="text-white/45 text-xs hover:text-white transition-colors"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 px-6 py-8 md:px-10">
        <div className="max-w-6xl mx-auto w-full">
          <RunnerLoginBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
